import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { WidgetFrame } from "@/dashboard/WidgetFrame";
import { useMyOpenTasks } from "../hooks/useWorkTasks";
import { TaskDueDatePill } from "./TaskDueDatePill";
import { TaskPriorityBadge } from "./TaskPriorityBadge";

interface Props {
  limit?: number;
}

/**
 * Tasks Module v1.0 § 7.5 — top N open tasks for the current user, ordered
 * by due date asc then priority desc (server-side ordering). Rendered in the
 * dashboard's personal band inside the shared WidgetFrame chrome.
 */
export function MyOpenTasksWidget({ limit = 5 }: Props) {
  const q = useMyOpenTasks(limit);

  return (
    <WidgetFrame
      title="My open tasks"
      to="/tasks"
      isLoading={q.isLoading}
      isError={q.isError}
      isEmpty={q.data?.length === 0}
      emptyText="All caught up. No open tasks."
    >
      {q.data && q.data.length > 0 && (
        <ul className="flex flex-col gap-space-2 m-0 p-0 list-none">
          {q.data.map((t) => (
            <li key={t.id}>
              <Link
                to={`/tasks/${t.id}`}
                className="flex items-center justify-between gap-space-2 rounded-md p-space-2 -mx-space-2 hover:bg-white-warm"
              >
                <div className="flex items-start gap-space-2 min-w-0">
                  <CheckCircle2
                    size={16}
                    strokeWidth={1.5}
                    className="text-gold-shadow mt-[2px] shrink-0"
                    aria-hidden
                  />
                  <span className="text-small text-onyx truncate">{t.title}</span>
                </div>
                <div className="flex items-center gap-space-2 shrink-0">
                  <TaskPriorityBadge priority={t.priority} />
                  {t.dueDate && <TaskDueDatePill dueDate={t.dueDate} />}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </WidgetFrame>
  );
}
